/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import api from "api";
import { toast } from "react-toastify";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Badge,
} from "reactstrap";
import { Store, UpdateStore } from "StoreContext";
import { getMediaUrl } from "config";

const OPTIONS = [
  {
    value: "none",
    title: "None",
    description: "No notifications when a redemption happens.",
    disabled: false,
  },
  {
    value: "email",
    title: "Email Notifications",
    description: "When an order is redeemed, you'll get an email automatically.",
    disabled: false,
  },
  {
    value: "shipstation",
    title: "ShipStation Integration",
    description:
      "Push redemptions straight to ShipStation as orders. Configure in Integrations →",
    // Selectable but the full push wiring is in progress. The credential
    // form lives on the (planned) /integrations page; selecting this here
    // sets the routing intent.
    disabled: false,
    badge: "New",
  },
];

const isValidEmail = (e) => {
  if (!e) return false;
  const v = String(e).trim();
  return v.length > 0 && v.includes("@") && v.includes(".");
};

// Embeddable form. Renders just the option list + save button — no Container/Card
// wrapper, so callers can drop it inside a Modal or another page.
// `onSaved` is fired after a successful save (used by the modal to auto-close).
export const OrderNotificationsForm = ({ onSaved } = {}) => {
  const store = Store();
  const updateStore = UpdateStore();
  const { user } = store || {};

  const [type, setType] = useState("none");
  const [email, setEmail] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [contactUrl, setContactUrl] = useState("");
  // Numeric brand-side knobs. Stored as strings while editing so the input
  // can be empty mid-edit; coerced + clamped on save (matches API limits).
  const [welcomeBonus, setWelcomeBonus] = useState("");
  const [lowStockThreshold, setLowStockThreshold] = useState("");
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  // Logo: a freshly-picked File replaces the saved logo on next save. Preview
  // shows the new file via Object URL when present, otherwise the saved logo.
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const fileInputRef = useRef(null);
  // Test email state
  const [testTo, setTestTo] = useState("");
  const [testSending, setTestSending] = useState(false);

  useEffect(() => {
    if (user) {
      setType(user.orderNotificationType || "none");
      setEmail(user.orderNotificationEmail || "");
      setReplyTo(user.replyToEmail || "");
      setContactUrl(user.contactPageUrl || "");
      setTestTo(user.orderNotificationEmail || user.email || "");
      setWelcomeBonus(
        typeof user.welcomeBonusPoints === "number" && user.welcomeBonusPoints > 0
          ? String(user.welcomeBonusPoints)
          : "",
      );
      setLowStockThreshold(
        typeof user.lowStockThreshold === "number" && user.lowStockThreshold > 0
          ? String(user.lowStockThreshold)
          : "",
      );
    }
  }, [user]);

  // Generate/revoke object URL when a new logo file is picked.
  useEffect(() => {
    if (!logoFile) { setLogoPreviewUrl(""); return; }
    const url = URL.createObjectURL(logoFile);
    setLogoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const savedLogoUrl = user?.logo ? getMediaUrl(user.logo) : "";
  const displayedLogoUrl = logoPreviewUrl || savedLogoUrl;

  const emailInvalid = type === "email" && touched && !isValidEmail(email);
  const saveDisabled =
    saving || (type === "email" && !isValidEmail(email));

  const handleSelect = (value) => {
    const opt = OPTIONS.find((o) => o.value === value);
    if (!opt || opt.disabled) return;
    setType(value);
  };

  const handleSave = async () => {
    if (!user?._id) return;
    setTouched(true);
    if (type === "email" && !isValidEmail(email)) {
      toast.error("Please enter a valid notification email.");
      return;
    }
    // Validate reply-to (optional but if set must be valid email)
    const replyToTrim = replyTo.trim();
    if (replyToTrim && (!replyToTrim.includes("@") || !replyToTrim.includes("."))) {
      toast.error("Reply-to email must be a valid address.");
      return;
    }
    const contactTrim = contactUrl.trim();
    if (contactTrim && !/^https?:\/\//i.test(contactTrim)) {
      toast.error("Contact page URL must start with http:// or https://.");
      return;
    }
    setSaving(true);
    try {
      // updateBrand backend accepts multipart/form-data with optional `logo`
      // file via `upload.single('logo')`. Required form fields like `name`
      // need to be present so the existing duplicate-name check passes.
      const fd = new FormData();
      if (user.name) fd.append("name", user.name);
      if (user.email) fd.append("email", user.email);
      if (user.url) fd.append("url", user.url);
      fd.append("orderNotificationType", type);
      fd.append("orderNotificationEmail", type === "email" ? email.trim() : "");
      fd.append("replyToEmail", replyToTrim);
      fd.append("contactPageUrl", contactTrim);
      // Welcome bonus + low-stock threshold — clamp to schema limits on
      // submit. Empty string = 0 = disabled.
      const wb = Math.max(0, Math.min(10000, Math.floor(Number(welcomeBonus) || 0)));
      const ls = Math.max(0, Math.min(100000, Math.floor(Number(lowStockThreshold) || 0)));
      fd.append("welcomeBonusPoints", String(wb));
      fd.append("lowStockThreshold", String(ls));
      if (logoFile) fd.append("logo", logoFile);

      // Integration credentials moved to the Integrations section — the API
      // sanitizer treats `integrations` as a partial blob, so omitting it
      // here doesn't wipe saved Klaviyo/ShipStation creds. axios sets the
      // multipart Content-Type (with boundary) automatically.
      const res = await api("put", `/users/brand/${user._id}`, fd);
      if (res?.success) {
        toast.success("Order notification settings saved.");
        if (res.user && updateStore) {
          updateStore({ user: res.user });
        }
        setLogoFile(null);
        if (typeof onSaved === "function") onSaved();
      } else {
        toast.error(res?.message || "Failed to save settings.");
      }
    } catch (err) {
      // axios interceptor already toasts; no-op
    } finally {
      setSaving(false);
    }
  };

  // Fire a sample order_placed + order_fulfilled pair to the chosen address.
  // The backend gates on logo + replyTo + contactPage being set (same gates as
  // the live pipeline), so a missing field surfaces here as a helpful 400.
  const handleSendTest = async () => {
    if (testSending) return;
    const target = (testTo || "").trim();
    if (!isValidEmail(target)) {
      toast.error("Enter a valid recipient email for the test.");
      return;
    }
    setTestSending(true);
    try {
      const res = await api("post", "/users/test-order-email", { to: target });
      if (res?.success) {
        toast.success(`Test emails sent to ${target}. Check the inbox in a minute.`);
      } else if (res?.missing?.length) {
        toast.error(`Save these settings first: ${res.missing.join(", ")}`);
      } else {
        toast.error(res?.message || "Test email failed — check the email log.");
      }
    } catch (err) {
      // axios interceptor handles the toast for most errors. The dev-skipped
      // 503 case has a structured payload — surface that explicitly so the
      // admin understands it's an env issue, not a code bug.
      const data = err?.response?.data;
      if (data?.devSkipped) {
        toast.warn(data.message || "Email sending disabled in this environment.", { autoClose: 7000 });
      }
    } finally {
      setTestSending(false);
    }
  };

  return (
    <div>
      {OPTIONS.map((opt) => {
        const selected = type === opt.value;
        const rowStyle = {
          border: selected
            ? "1px solid #362FD8"
            : "1px solid #E5E7EB",
          borderRadius: 8,
          padding: "14px 16px",
          marginBottom: 12,
          background: selected ? "#F5F4FF" : "#FFFFFF",
          cursor: opt.disabled ? "not-allowed" : "pointer",
          opacity: opt.disabled ? 0.55 : 1,
          transition: "border-color 120ms ease, background 120ms ease",
        };
        return (
          <div
            key={opt.value}
            style={rowStyle}
            onClick={() => handleSelect(opt.value)}
          >
            <FormGroup check className="mb-0">
              <Label
                check
                style={{
                  cursor: opt.disabled ? "not-allowed" : "pointer",
                  width: "100%",
                }}
              >
                <Input
                  type="radio"
                  name="orderNotificationType"
                  value={opt.value}
                  checked={selected}
                  disabled={opt.disabled}
                  onChange={() => handleSelect(opt.value)}
                />
                <span style={{ fontWeight: 600, color: "#111827" }}>
                  {opt.title}
                </span>
                {opt.disabled && (
                  <Badge
                    pill
                    style={{
                      marginLeft: 8,
                      background: "#E5E7EB",
                      color: "#374151",
                      fontWeight: 500,
                      padding: "4px 8px",
                    }}
                  >
                    Coming soon
                  </Badge>
                )}
                <div
                  style={{
                    marginTop: 4,
                    color: "#6B7280",
                    fontSize: "0.85rem",
                  }}
                >
                  {opt.description}
                </div>
              </Label>
            </FormGroup>

            {opt.value === "email" && selected && (
              <FormGroup style={{ marginTop: 12, marginBottom: 0 }}>
                <Label
                  for="orderNotificationEmail"
                  style={{ fontWeight: 500, color: "#374151" }}
                >
                  Notification email
                </Label>
                <Input
                  id="orderNotificationEmail"
                  type="email"
                  placeholder="orders@yourbrand.com"
                  value={email}
                  invalid={emailInvalid}
                  onBlur={() => setTouched(true)}
                  onChange={(e) => setEmail(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                {emailInvalid && (
                  <div
                    style={{
                      color: "#DC2626",
                      fontSize: "0.8rem",
                      marginTop: 4,
                    }}
                  >
                    Please enter a valid email address.
                  </div>
                )}
              </FormGroup>
            )}
          </div>
        );
      })}

      {/* Customer-facing email + contact info. Required before any order
          email goes out — the API gates sends on them being set. */}
      <div style={{ marginTop: 24, paddingTop: 18, borderTop: "1px solid #E5E7EB" }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, color: "#111827", margin: "0 0 4px" }}>Customer order emails</h4>
        <p style={{ fontSize: 12, color: "#6B7280", margin: "0 0 14px", lineHeight: 1.45 }}>
          Order confirmation + tracking emails go out from VeriScan&apos;s no-reply address. Set the reply-to and
          contact page so customers can reach <em>your</em> team.
        </p>

        <FormGroup>
          <Label style={{ fontWeight: 500, color: "#374151", fontSize: 13 }}>Brand logo <span style={{ color: "#DC2626" }}>*</span></Label>
          <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 10, border: "1px solid #E5E7EB", borderRadius: 8, background: "#FAFAFB" }}>
            <div style={{ width: 64, height: 64, borderRadius: 8, background: "#fff", border: "1px solid #E5E7EB", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
              {displayedLogoUrl ? (
                <img src={displayedLogoUrl} alt="Brand logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
              ) : (
                <span style={{ fontSize: 11, color: "#9CA3AF", textAlign: "center", padding: "0 4px" }}>No logo</span>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  if (!f) return;
                  if (f.size > 2 * 1024 * 1024) {
                    toast.error("Logo must be under 2 MB.");
                    return;
                  }
                  setLogoFile(f);
                }}
              />
              <Button
                size="sm"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                style={{ background: "#fff", borderColor: "#D1D5DB", color: "#1C1C1C", fontSize: 13 }}
              >
                {savedLogoUrl || logoFile ? "Replace logo" : "Upload logo"}
              </Button>
              {logoFile && (
                <button
                  type="button"
                  onClick={() => { setLogoFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                  style={{ background: "transparent", border: "none", color: "#6B7280", fontSize: 12, marginLeft: 8, cursor: "pointer", textDecoration: "underline" }}
                >
                  Discard
                </button>
              )}
              <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                PNG/JPG/SVG, &lt; 2 MB. Shown at the top of every customer order email.
              </div>
            </div>
          </div>
        </FormGroup>

        <FormGroup>
          <Label style={{ fontWeight: 500, color: "#374151", fontSize: 13 }}>Reply-to email <span style={{ color: "#DC2626" }}>*</span></Label>
          <Input type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="orders@yourbrand.com" maxLength={254} />
          <small style={{ color: "#888", fontSize: 11 }}>Replies from customers go to this address.</small>
        </FormGroup>
        <FormGroup>
          <Label style={{ fontWeight: 500, color: "#374151", fontSize: 13 }}>Contact page URL <span style={{ color: "#DC2626" }}>*</span></Label>
          <Input type="url" value={contactUrl} onChange={(e) => setContactUrl(e.target.value)} placeholder="https://yourbrand.com/contact" maxLength={2048} />
          <small style={{ color: "#888", fontSize: 11 }}>Linked from the email footer.</small>
        </FormGroup>

        <div style={{ marginTop: 24, marginBottom: 8, fontSize: 13, fontWeight: 600, color: "#111827" }}>
          Rewards behavior
        </div>
        <FormGroup>
          <Label style={{ fontWeight: 500, color: "#374151", fontSize: 13 }}>First-scan welcome bonus</Label>
          <Input
            type="number"
            min={0}
            max={10000}
            step={1}
            value={welcomeBonus}
            onChange={(e) => setWelcomeBonus(e.target.value)}
            placeholder="0"
          />
          <small style={{ color: "#888", fontSize: 11 }}>
            One-time bonus points granted the first time a customer ever earns points for your brand. Stacks with the code's points. Set to 0 to disable. Max 10,000.
          </small>
        </FormGroup>
        <FormGroup>
          <Label style={{ fontWeight: 500, color: "#374151", fontSize: 13 }}>Low-stock alert threshold</Label>
          <Input
            type="number"
            min={0}
            max={100000}
            step={1}
            value={lowStockThreshold}
            onChange={(e) => setLowStockThreshold(e.target.value)}
            placeholder="0"
            disabled={type !== "email"}
          />
          <small style={{ color: "#888", fontSize: 11 }}>
            Send an alert to the notification email above when a reward product (or sized variant) drops to this level after a redemption. Set to 0 to disable. Re-arms 7 days after a send, or sooner once the product is restocked above this level.
            {type !== "email" && (
              <span style={{ color: "#b45309", display: "block", marginTop: 4 }}>
                Requires <strong>Email Notifications</strong> selected above to deliver alerts.
              </span>
            )}
          </small>
        </FormGroup>
      </div>

      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
        <Button
          color="primary"
          onClick={handleSave}
          disabled={saveDisabled}
          style={{ background: "#362FD8", borderColor: "#362FD8" }}
        >
          {saving ? (
            <>
              <Spinner size="sm" /> &nbsp;Saving...
            </>
          ) : (
            "Save settings"
          )}
        </Button>
      </div>

      {/* Test send — uses the brand's saved config (so users must Save first
          before a logo/replyTo/contact change is reflected in the test). */}
      <div style={{ marginTop: 28, padding: 14, border: "1px dashed #D1D5DB", borderRadius: 8, background: "#F9FAFB" }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: "#111827", margin: "0 0 4px" }}>Send a test email</h4>
        <p style={{ fontSize: 12, color: "#6B7280", margin: "0 0 10px", lineHeight: 1.45 }}>
          We&apos;ll send a sample <strong>Order placed</strong> + <strong>Order shipped</strong> pair using your saved logo, reply-to, and contact link.
          Save changes first if you want them reflected.
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" }}>
          <Input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@yourbrand.com"
            style={{ flex: 1, minWidth: 200, fontSize: 13 }}
          />
          <Button
            onClick={handleSendTest}
            disabled={testSending || !isValidEmail(testTo)}
            style={{ background: "#1C1C1C", borderColor: "#1C1C1C", color: "#fff", fontSize: 13 }}
          >
            {testSending ? (<><Spinner size="sm" /> &nbsp;Sending…</>) : "Send test"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Standalone page wrapper, kept for the deep-link route.
const OrderNotifications = () => {
  return (
    <Container fluid>
      <Row>
        <div className="col">
          <Card className="shadow">
            <CardHeader className="border-0">
              <h3 className="mb-0">Order Notifications</h3>
              <p
                className="mb-0 mt-2"
                style={{ color: "#6B7280", fontSize: "0.875rem" }}
              >
                Choose how you want to be notified when a customer redeems
                points for an order. Only one option can be active at a time.
              </p>
            </CardHeader>
            <CardBody>
              <OrderNotificationsForm />
            </CardBody>
          </Card>
        </div>
      </Row>
    </Container>
  );
};

export default OrderNotifications;
